import axios from "axios";
import {
  MultiAirportDateRangeConfig,
  MultiAirportDateRangeFailure,
  MultiAirportDateRangeFailureStage,
  MultiAirportDateRangeResult,
  MultiAirportDateRangeTarget,
} from "../../models/airports/AirportConfig";
import {
  IApiResourceResponse,
  IKpiSettingsResponse,
} from "../../models/app-settings/AppSettingsModels";
import { AIRPORT_CODE_PARAMETER } from "../../utils/common/Constants";
import {
  calculateAirportDateTimeRange,
  calculateAirportKpiDateRange,
  getOtpKpiSetting,
} from "../../utils/multi-airports/MultiAirportDateRangeHelper";

const getAirportCode = (airport: MultiAirportDateRangeConfig): string =>
  (airport.iataCode || airport.code || airport.id || "").trim();

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "Unknown error";

const buildSettingsUrl = (
  endpoint: string | undefined,
  airportCode: string
) => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
  if (!endpoint) {
    throw new Error("Settings API endpoint is not configured");
  }

  return `${baseUrl}${endpoint}`.replace(AIRPORT_CODE_PARAMETER, airportCode);
};

const getHeaders = (accessToken: string) => ({
  "x-api-key": process.env.REACT_APP_APIM_KEY,
  Authorization: `Bearer ${accessToken}`,
  "Content-Type": "application/json;charset=UTF-8",
});

interface AirportDateRangeLoadResult {
  otp?: MultiAirportDateRangeResult["payload"]["otp"][number];
  flights?: MultiAirportDateRangeResult["payload"]["flights"][number];
  errors: MultiAirportDateRangeFailure[];
}

const createFailure = (
  airportCode: string,
  target: MultiAirportDateRangeTarget,
  stage: MultiAirportDateRangeFailureStage,
  reason: unknown
): MultiAirportDateRangeFailure => ({
  airportCode,
  target,
  stage,
  message: getErrorMessage(reason),
});

const loadAirportDateRange = async (
  airport: MultiAirportDateRangeConfig,
  accessToken: string,
  currentTime: Date,
  signal?: AbortSignal
): Promise<AirportDateRangeLoadResult> => {
  const airportCode = getAirportCode(airport);
  const result: AirportDateRangeLoadResult = { errors: [] };
  if (!airportCode) {
    result.errors.push(
      createFailure(
        airportCode,
        "all",
        "calculation",
        new Error("Airport code is unavailable")
      )
    );
    return result;
  }

  const headers = getHeaders(accessToken);
  const loadKpiSettings = async () => {
    const url = buildSettingsUrl(
      process.env.REACT_APP_API_GET_KPI_TIME,
      airportCode
    );
    return (await axios.get<IKpiSettingsResponse>(url, { headers, signal }))
      .data;
  };
  const loadDateTimeSettings = async () => {
    const url = buildSettingsUrl(
      process.env.REACT_APP_API_DATE_TIME_CONFIG,
      airportCode
    );
    return (await axios.get<IApiResourceResponse>(url, { headers, signal }))
      .data;
  };

  const [kpiSettingsResult, dateTimeSettingsResult] = await Promise.allSettled([
    loadKpiSettings(),
    loadDateTimeSettings(),
  ]);

  if (dateTimeSettingsResult.status === "fulfilled") {
    try {
      result.flights = calculateAirportDateTimeRange(
        airport,
        currentTime,
        dateTimeSettingsResult.value
      );
    } catch (error) {
      result.errors.push(
        createFailure(airportCode, "flights", "calculation", error)
      );
    }
  } else {
    result.errors.push(
      createFailure(
        airportCode,
        "flights",
        "date-time-settings",
        dateTimeSettingsResult.reason
      )
    );
  }

  if (kpiSettingsResult.status === "rejected") {
    result.errors.push(
      createFailure(
        airportCode,
        "otp",
        "kpi-settings",
        kpiSettingsResult.reason
      )
    );
    return result;
  }

  try {
    const otpSetting = getOtpKpiSetting(kpiSettingsResult.value);
    if (
      !otpSetting.isRollingActive &&
      dateTimeSettingsResult.status === "rejected"
    ) {
      result.errors.push(
        createFailure(
          airportCode,
          "otp",
          "date-time-settings",
          dateTimeSettingsResult.reason
        )
      );
      return result;
    }

    result.otp = calculateAirportKpiDateRange(
      airport,
      currentTime,
      kpiSettingsResult.value,
      dateTimeSettingsResult.status === "fulfilled"
        ? dateTimeSettingsResult.value
        : undefined
    );
  } catch (error) {
    result.errors.push(createFailure(airportCode, "otp", "calculation", error));
  }

  return result;
};

/**
 * Loads the settings needed to calculate OTP and flights ranges per airport.
 * Both settings requests run concurrently, and failures are isolated by range.
 */
export const loadMultiAirportDateRanges = async (
  airports: MultiAirportDateRangeConfig[],
  accessToken: string,
  currentTime: Date,
  signal?: AbortSignal
): Promise<MultiAirportDateRangeResult> => {
  if (!accessToken) {
    return {
      payload: { otp: [], flights: [] },
      errors: airports.map((airport) => ({
        airportCode: getAirportCode(airport),
        target: "all",
        stage: "authentication",
        message: "Access token cannot be empty",
      })),
    };
  }

  const airportResults = await Promise.allSettled(
    airports.map((airport) =>
      loadAirportDateRange(airport, accessToken, currentTime, signal)
    )
  );

  return airportResults.reduce<MultiAirportDateRangeResult>(
    (result, airportResult, index) => {
      if (airportResult.status === "rejected") {
        result.errors.push(
          createFailure(
            getAirportCode(airports[index]),
            "all",
            "calculation",
            airportResult.reason
          )
        );
        return result;
      }

      if (airportResult.value.otp) {
        result.payload.otp.push(airportResult.value.otp);
      }
      if (airportResult.value.flights) {
        result.payload.flights.push(airportResult.value.flights);
      }
      result.errors.push(...airportResult.value.errors);
      return result;
    },
    { payload: { otp: [], flights: [] }, errors: [] }
  );
};
